import React from "react";

/**
 * Subtle BENO motif elements — original decorative SVGs *inspired by* the
 * brand language (waves, sun rays, arc geometry) but NEVER copies of the
 * official logo. Used at ultra-low opacity as section dividers, hero
 * watermarks, and gentle ornamentation.
 *
 * Rule of thumb: if these elements draw attention, they are too loud.
 */

/** Thin horizontal wave divider — for between major sections. */
export function WaveDivider({ className = "", color = "#C17F59", opacity = 0.18 }) {
  return (
    <svg
      viewBox="0 0 600 18"
      preserveAspectRatio="none"
      className={`block w-full h-3 ${className}`}
      role="presentation"
      aria-hidden="true"
      data-testid="brand-wave-divider"
    >
      <path
        d="M0 9 Q 30 2, 60 9 T 120 9 T 180 9 T 240 9 T 300 9 T 360 9 T 420 9 T 480 9 T 540 9 T 600 9"
        stroke={color}
        strokeWidth="1"
        fill="none"
        opacity={opacity}
        strokeLinecap="round"
      />
      <path
        d="M0 13 Q 30 6, 60 13 T 120 13 T 180 13 T 240 13 T 300 13 T 360 13 T 420 13 T 480 13 T 540 13 T 600 13"
        stroke={color}
        strokeWidth="1"
        fill="none"
        opacity={opacity * 0.6}
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Faint sun-ray fan — used as a hero watermark or behind Tonight's Pour. */
export function SunRayMark({ size = 240, className = "", color = "#C17F59", opacity = 0.07 }) {
  const rays = 24;
  const cx = 50;
  const cy = 50;
  const inner = 18;
  const outer = 48;
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={`pointer-events-none ${className}`}
      role="presentation"
      aria-hidden="true"
      data-testid="brand-sun-ray"
      style={{ opacity }}
    >
      <circle cx={cx} cy={cy} r={inner * 0.55} fill="none" stroke={color} strokeWidth="0.4" />
      {Array.from({ length: rays }, (_, i) => {
        const a = (i / rays) * Math.PI * 2;
        const x1 = cx + Math.cos(a) * inner;
        const y1 = cy + Math.sin(a) * inner;
        const x2 = cx + Math.cos(a) * outer;
        const y2 = cy + Math.sin(a) * outer;
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={color}
            strokeWidth="0.35"
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}

/** Soft arc — minimal geometric breath, used at section starts. */
export function ArcAccent({ size = 120, className = "", color = "#C17F59", opacity = 0.18 }) {
  return (
    <svg
      viewBox="0 0 120 60"
      width={size}
      height={size / 2}
      className={`pointer-events-none ${className}`}
      role="presentation"
      aria-hidden="true"
      data-testid="brand-arc"
      style={{ opacity }}
    >
      <path
        d="M5 55 Q 60 -10, 115 55"
        stroke={color}
        strokeWidth="0.8"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}
